import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import {
  countDadosCadastrais,
  countScore,
  formatDocument,
  getColorScore,
  getImageURLScoreSerasa,
} from "../../../utils/utils";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { TfiBarChart } from "react-icons/tfi";
import { LuBuilding2 } from "react-icons/lu";
import { BsBank } from "react-icons/bs";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
  GrMoney,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { BiLineChartDown } from "react-icons/bi";
import ScoreGaugeApex from "../../common/query/Score";
import Sociedade from "../../common/query/Sociedade";
import moment from "moment";

const parseProbability = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value !== "string") return null;

  const normalized = value.replace(",", ".").replace("%", "").trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateTenure = (value) => {
  if (!value) return null;
  const parsed = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"]);
  if (!parsed.isValid()) return null;
  const now = moment();
  const years = now.diff(parsed, "years");
  const months = now.diff(parsed.clone().add(years, "years"), "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return "menos de 1 mês";
  return parts.join(" e ");
};

const CrednetQuery = ({ data, dataFull }) => {
  const prob = parseProbability(data?.score?.probabilidadeInadimplencia);

  let riskLabel = "Indefinido";
  let riskClasses = {
    chip: "bg-gray-100 text-gray-700",
    ring: "from-slate-50 to-slate-100",
    dotActive: "bg-gray-300",
    dotIdle: "bg-gray-100",
  };

  if (prob !== null) {
    if (prob < 10) {
      riskLabel = "Baixo";
      riskClasses = {
        chip: "bg-green-100 text-green-700",
        ring: "from-green-50 to-teal-50",
        dotActive: "bg-green-500",
        dotIdle: "bg-green-100",
      };
    } else if (prob < 25) {
      riskLabel = "Moderado";
      riskClasses = {
        chip: "bg-amber-100 text-amber-700",
        ring: "from-amber-50 to-yellow-50",
        dotActive: "bg-amber-500",
        dotIdle: "bg-amber-100",
      };
    } else if (prob < 60) {
      riskLabel = "Alto";
      riskClasses = {
        chip: "bg-orange-100 text-orange-700",
        ring: "from-orange-50 to-amber-50",
        dotActive: "bg-orange-500",
        dotIdle: "bg-orange-100",
      };
    } else {
      riskLabel = "Altíssimo";
      riskClasses = {
        chip: "bg-rose-100 text-rose-700",
        ring: "from-rose-50 to-red-50",
        dotActive: "bg-rose-500",
        dotIdle: "bg-rose-100",
      };
    }
  }

  const cadastro = data?.dadosCadastrais || {};
  const foundationDate = cadastro.dataFundacao;
  const tempoAbertura = calculateTenure(foundationDate);
  const situacao = cadastro.situacao || "";
  const isAtivo = situacao.toUpperCase() === "ATIVO";
  const birthDateRaw = cadastro.dataNascimento;
  const birthMoment = birthDateRaw
    ? moment(birthDateRaw, ["YYYY-MM-DD", "DD/MM/YYYY"])
    : null;
  const isBirthValid = birthMoment?.isValid();
  const birthLabel = isBirthValid ? birthMoment.format("DD/MM/YYYY") : null;
  const birthYears = isBirthValid ? moment().diff(birthMoment, "years") : null;
  const ageLabel =
    birthYears != null
      ? `${birthYears} ano${birthYears === 1 ? "" : "s"}`
      : null;

  return (
    <div>
      <>
        <TitleStripe>
          <div className="flex items-center gap-2">
            {data?.dadosCadastrais?.cpf ? (
              <FiUserCheck className="text-blue-700" />
            ) : (
              <LuBuilding2 className="text-blue-700" />
            )}

            <span>Dados Cadastrais / Base II</span>
          </div>
        </TitleStripe>

        {cadastro.nome && (
          <div className="mt-4">
            <div className="">
              <div className="flex gap-3 flex-row items-start justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-400">
                    Pessoa Física
                  </p>
                  <p className="text-xl font-semibold text-gray-900">
                    {cadastro.nome}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${
                    cadastro.situacao
                      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                      : "border-gray-200 bg-gray-50 text-gray-500"
                  }`}
                >
                  {cadastro.situacao || "Situação não informada"}
                </span>
              </div>

              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Nascimento
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {birthLabel || "—"}
                  </p>
                  {ageLabel && (
                    <p className="text-[11px] text-gray-500 mt-1">{ageLabel}</p>
                  )}
                </div>
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Nome da mãe
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {cadastro.nomeMae || "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Documento
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDocument(cadastro.cpf) || "Sem documento"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {cadastro.razaoSocial && (
          <div className="mt-4">
            <div className="">
              <div className="flex gap-3 flex-row items-start justify-between">
                <div>
                  <p className="text-gray-500 text-[11px] uppercase tracking-wide">
                    Razão Social
                  </p>
                  <p className="text-xl font-semibold text-gray-900">
                    {cadastro.razaoSocial || "Indisponível"}
                  </p>
                </div>
                <ModalReceitaFederal cnpj={cadastro.cnpj} />
              </div>

              <div className="mt-3 grid gap-4 grid-cols-[2fr,1fr]">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                        Fundação
                      </p>
                      <p className="text-xl font-semibold text-slate-900">
                        {foundationDate || "Indisponível"}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        {tempoAbertura
                          ? `Aberta há ${tempoAbertura}`
                          : "Data de abertura ausente"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 grid-cols-2">
                    <div className="rounded-2xl border border-gray-200 bg-white p-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                        Situação
                      </p>
                      <p className="font-semibold text-sm text-slate-900">
                        {cadastro.situacao || "Indisponível"}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white p-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                        Localização
                      </p>
                      <p className="font-semibold text-sm text-slate-900">
                        {cadastro.cidade || "—"} {cadastro.uf || ""}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Contato / CNPJ
                  </p>
                  <p className="text-lg font-semibold text-slate-900 mt-1">
                    {cadastro.cnpj
                      ? formatDocument(cadastro.cnpj)
                      : "Indisponível"}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-2">
                    {cadastro.telefone
                      ? `Fone ${cadastro.telefone}`
                      : cadastro.email
                      ? cadastro.email
                      : "Contato não disponível"}
                  </p>
                  {cadastro.email && cadastro.telefone && (
                    <p className="text-[11px] text-gray-400 mt-2">
                      {cadastro.email}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {data?.dadosCadastrais?.cpf && (
          <div>
            <TitleStripe className="mb-5">
              <div className="flex items-center gap-2">
                <LuBuilding2 className="text-blue-700" />
                <span>Participação em empresas / Base II</span>
              </div>
            </TitleStripe>
            {data?.participacaoEmEmpresas?.length > 0 ||
            data?.socios?.length > 0 ? (
              <ParticipacaoEmEmpresas
                empresas={data?.participacaoEmEmpresas || data?.socios}
              />
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}
        {data?.dadosCadastrais?.cnpj && (
          <div>
            <TitleStripe className="mb-5">
              <div className="flex items-center gap-2">
                <FiUsers className="text-blue-700" />
                <span>Quadro Societário / Base II</span>
              </div>
            </TitleStripe>
            {data?.socios?.length > 0 ? (
              <Sociedade socios={data?.socios} />
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}

        {data?.dadosCadastrais?.cnpj &&
          data?.dadosCadastrais?.administradores && (
            <div>
              <TitleStripe className="mb-5">
                <div className="flex items-center gap-2">
                  <FiUsers className="text-blue-700" />
                  <span>Administradores / Base II</span>
                </div>
              </TitleStripe>
              {data?.administradores?.length > 0 ? (
                <Sociedade socios={data?.administradores} />
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
      </>

      {data?.score?.score && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <TfiBarChart className="text-blue-700" />{" "}
              <span>Score de risco / Base II</span>
            </div>
          </TitleStripe>
          <div className="flex  flex-row items-center gap-6">
            {data?.score?.score ? (
              <>
                <ScoreGaugeApex
                  score={+data?.score?.score}
                  className="flex flex-col items-center md:w-1/3"
                />
                <div className="w-2/3 relative rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                  {/* fundo sutil */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${riskClasses.ring}`}
                  />

                  <div className="relative p-4">
                    {/* header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📉</span>
                        <div className="text-sm font-semibold text-gray-700">
                          Probabilidade de Inadimplência
                        </div>
                      </div>

                      {/* badges à direita */}
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-semibold ${riskClasses.chip}`}
                        >
                          {riskLabel}
                        </span>
                        <span className="text-sm px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                          {prob !== null ? `${prob}%` : "--"}
                        </span>
                      </div>
                    </div>

                    {/* escala de risco com 4 pontos (não é barra de progresso) */}
                    <div className="flex items-center gap-2 mb-3">
                      {[0, 1, 2, 3].map((i) => {
                        const thresholds = [10, 25, 50, 100];
                        const active =
                          prob !== null ? prob >= thresholds[i] : false;
                        return (
                          <div
                            key={i}
                            className={`h-2 w-full rounded-full transition-colors ${
                              active
                                ? riskClasses.dotActive
                                : riskClasses.dotIdle
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm mt-4 w-full">
                <p className="font-medium">
                  Não foi possível calcular o score desta empresa.
                </p>
                <p className="mt-1">
                  É necessário verificar as outras informações disponíveis no
                  relatório para uma análise completa.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Pendências Financeiras / Base II</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <>
            <PendenciasFinanceiras
              quantidadeTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
              }
              valorTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal
              }
              pendenciasFinanceiras={data?.pendenciasFinanceiras}
            />
          </>
        ) : (
          <NoOcurrence />
        )}

        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <GrDocumentExcel className="text-blue-700" />
              <span>Títulos protestados / Base II</span>
            </div>
          </TitleStripe>
          {data?.protestos?.length > 0 ? (
            <Protestos
              quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
              valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
              protestos={data?.protestos}
              temVencimento={false}
            />
          ) : (
            <NoOcurrence />
          )}
        </div>
      </div>

      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo / Base II</span>
          </div>
        </TitleStripe>

        {data?.chequesSemFundo?.length > 0 ? (
          <>
            <ChequesSemFundos chequesSemFundos={data?.chequesSemFundo} />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
    </div>
  );
};

export default CrednetQuery;
